import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ShoppingCart, TrendingUp, Clock, CheckCircle, XCircle, Eye, RefreshCw, Trash2, Plus } from 'lucide-react';
import { getStatusLabel, getStatusVariant, getStatusColor, type OrderStatus } from '@/lib/statusMapping';
import { toast } from 'sonner';
import OrderDetailModal from './OrderDetailModal';
import OrderCreateDialog from './OrderCreateDialog';
import { truncateString } from '@/lib/utils';


interface Order {
  id: string;
  user_id: string;
  total_amount: number;
  total_delivery_cost: number;
  status: string;
  created_at: string;
  order_number: string | null;
  billing_address?: any;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
    email: string;
  };
  sub_orders?: Array<{
    id: string;
    products: {
      name: string;
    };
    quantity: number;
    unit_price: number;
    total_price: number;
    delivery_option: string;
  }>;
}

const OrdersModule = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [syncingOrderId, setSyncingOrderId] = useState<string | null>(null);
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null);
  const [cleaningStaleOrders, setCleaningStaleOrders] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Format order number with dashes (123456789 -> 123-456-789)
  const formatOrderNumber = (orderNumber: string | null) => {
    if (!orderNumber) return '';
    const cleaned = orderNumber.replace(/\D/g, '');
    return cleaned.replace(/(\d{3})(?=\d)/g, '$1-');
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      let query = supabase
        .from('orders')
        .select(`
          id, user_id, status, total_amount, total_delivery_cost, order_number, created_at, updated_at, billing_address,
          sub_orders (
            id,
            product_name,
            quantity,
            unit_price,
            total_price,
            delivery_option
          )
        `)
        .order('created_at', { ascending: false });

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter as any);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch user profiles separately
      const ordersWithProfiles = await Promise.all(
        (data || []).map(async (order) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name, email')
            .eq('id', order.user_id)
            .maybeSingle();

          // Transform sub_orders for product display only
          const transformedSubOrders = (order.sub_orders || []).map(subOrder => ({
            id: subOrder.id,
            products: {
              name: subOrder.product_name || 'מוצר לא זמין'
            },
            quantity: subOrder.quantity,
            unit_price: subOrder.unit_price,
            total_price: subOrder.total_price,
            delivery_option: subOrder.delivery_option
          }));

          // Handle potentially stringified billing_address
          let billingAddr = order.billing_address;
          if (typeof billingAddr === 'string') {
            try {
              billingAddr = JSON.parse(billingAddr);
            } catch (e) {
              console.error('Failed to parse billing_address', e);
            }
          }

          if (data && data.indexOf(order) === 0) {
            console.log('[OrdersModule] Sample Order Billing Address:', billingAddr);
          }

          return { ...order, billing_address: billingAddr, profiles: profile, sub_orders: transformedSubOrders } as Order;
        })
      );

      setOrders(ordersWithProfiles);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTotalRevenue = () => {
    return orders.reduce((sum, order) => sum + order.total_amount, 0);
  };

  const getOrdersThisWeek = () => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return orders.filter(order => new Date(order.created_at) > weekAgo).length;
  };

  const getAverageOrderValue = () => {
    if (orders.length === 0) return 0;
    return getTotalRevenue() / orders.length;
  };

  type OrderStatus = 'pending' | 'processing' | 'ready_for_pickup' | 'shipped' | 'completed' | 'cancelled';

  // Helper function to determine allowed statuses based on delivery options
  const getAllowedStatuses = (order: Order): OrderStatus[] => {
    const deliveryOptions = (order.sub_orders || []).map((so) => so.delivery_option || '');

    // Map delivery_option values to logical capabilities
    const hasDelivery = deliveryOptions.some((opt) =>
      ['delivery', 'delivery_only', 'both'].includes(opt)
    );
    const hasPickup = deliveryOptions.some((opt) =>
      ['pickup', 'pickup_only', 'both'].includes(opt)
    );

    const baseStatuses: OrderStatus[] = ['pending', 'processing', 'completed', 'cancelled'];

    if (hasDelivery && !hasPickup) {
      // Delivery only - can be shipped, but not ready_for_pickup
      return [...baseStatuses.slice(0, 2), 'shipped', ...baseStatuses.slice(2)];
    } else if (hasPickup && !hasDelivery) {
      // Pickup only - can be ready_for_pickup, but not shipped
      return [...baseStatuses.slice(0, 2), 'ready_for_pickup', ...baseStatuses.slice(2)];
    } else if (hasDelivery || hasPickup) {
      // Both or mixed - show both shipment-related statuses
      return [...baseStatuses.slice(0, 2), 'ready_for_pickup', 'shipped', ...baseStatuses.slice(2)];
    }

    // Fallback: no delivery info -> allow all options
    return [...baseStatuses.slice(0, 2), 'ready_for_pickup', 'shipped', ...baseStatuses.slice(2)];
  };

  const updateOrderStatus = async (orderId: string, newStatus: OrderStatus) => {
    try {
      // Find the order to get order number and check if status changed
      const order = orders.find(o => o.id === orderId);
      const oldStatus = order?.status;

      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus as any })
        .eq('id', orderId);

      if (error) throw error;

      // Send email notification for relevant status changes
      if (order && oldStatus !== newStatus && ['processing', 'ready_for_pickup', 'shipped', 'completed', 'cancelled'].includes(newStatus)) {
        try {
          await supabase.functions.invoke('send-order-status-email', {
            body: {
              orderId: orderId,
              newStatus: newStatus,
              orderNumber: order.order_number || orderId.slice(-6)
            }
          });
          console.log('Status email sent successfully');
        } catch (emailError) {
          console.error('Failed to send status email:', emailError);
          // Don't fail the status update if email fails
        }
      }

      // Refresh orders list
      fetchOrders();

      toast.success("הצלחה", {
        description: `סטטוס ההזמנה עודכן ל${getStatusLabel(newStatus)}`
      });
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error("שגיאה", {
        description: "לא ניתן לעדכן את סטטוס ההזמנה",
      });
    }
  };

  const resyncPaymentStatus = async (orderId: string) => {
    setSyncingOrderId(orderId);
    try {
      const { data, error } = await supabase.functions.invoke('reconcile-payment-status', {
        body: { orderId }
      });

      if (error) throw error;

      toast.success("הצלחה", {
        description: data?.message || "סטטוס התשלום סונכרן בהצלחה"
      });

      fetchOrders(); // Refresh the list
    } catch (error: any) {
      console.error('Error resyncing payment status:', error);
      toast.error("שגיאה", {
        description: error?.message || "לא ניתן לסנכרן את סטטוס התשלום",
      });
    } finally {
      setSyncingOrderId(null);
    }
  };

  const getOrderStatusBadge = (status: OrderStatus) => {
    return (
      <Badge
        variant="secondary"
        style={{
          backgroundColor: getStatusColor(status),
          color: 'white',
          border: 'none'
        }}
        className="text-xs"
      >
        {getStatusLabel(status)}
      </Badge>
    );
  };

  const deleteOrder = async (orderId: string, orderNumber: string) => {
    setDeletingOrderId(orderId);
    try {
      const { data, error } = await supabase.rpc('delete_order_cascade', {
        target_order_id: orderId
      });

      if (error) throw error;

      toast.success("הצלחה", {
        description: `הזמנה ${orderNumber} וכל הנתונים הקשורים נמחקו בהצלחה`,
      });

      // Refresh orders list
      fetchOrders();
    } catch (error: any) {
      console.error('Error deleting order:', error);
      toast.error("שגיאה", {
        description: error.message || "לא ניתן למחוק את ההזמנה",
      });
    } finally {
      setDeletingOrderId(null);
    }
  };

  const cleanupStaleOrders = async () => {
    setCleaningStaleOrders(true);
    try {
      const { data, error } = await supabase.rpc('cleanup_stale_pending_orders' as any, { hours_old: 24 });

      if (error) throw error;

      const cleanedCount = Array.isArray(data) ? data.length : 0;

      toast.success("הצלחה", {
        description: `${cleanedCount} הזמנות ישנות בוטלו`,
      });

      fetchOrders();
    } catch (error: any) {
      console.error('Error cleaning stale orders:', error);
      toast.error("שגיאה", {
        description: error?.message || "לא ניתן לבטל הזמנות ישנות",
      });
    } finally {
      setCleaningStaleOrders(false);
    }
  };

  // Refetch orders when status filter changes
  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const handleViewOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
  };

  // Filter orders based on search query
  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    const orderNumber = order.order_number?.toLowerCase() || '';
    const customerName = order.profiles?.first_name && order.profiles?.last_name
      ? `${order.profiles.first_name} ${order.profiles.last_name}`.toLowerCase()
      : ((order.billing_address?.firstName || order.billing_address?.lastName || order.billing_address?.first_name || order.billing_address?.last_name) || '').toLowerCase();
    const customerEmail = order.profiles?.email?.toLowerCase() || (order.billing_address?.email?.toLowerCase() || '');

    return orderNumber.includes(query) ||
      customerName.includes(query) ||
      customerEmail.includes(query);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">טוען הזמנות...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">ניהול הזמנות</h2>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 ml-2" />
          צור הזמנה חדשה
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">סה"כ הזמנות</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{orders.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">הכנסות כוללות</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ₪{getTotalRevenue().toLocaleString()}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">הזמנות השבוע</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{getOrdersThisWeek()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ממוצע הזמנה</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              ₪{getAverageOrderValue().toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                רשימת הזמנות
              </CardTitle>
            </div>

            {/* Search and filters row */}
            <div className="flex gap-2 flex-wrap">
              {/* Search input */}
              <input
                type="text"
                placeholder="חיפוש לפי מספר הזמנה, שם או אימייל..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 min-w-[250px] px-3 py-2 border rounded-md text-sm"
                dir="rtl"
              />

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={cleaningStaleOrders}
                  >
                    {cleaningStaleOrders ? (
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    ביטול הזמנות ישנות
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent dir="rtl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>האם אתה בטוח?</AlertDialogTitle>
                    <AlertDialogDescription>
                      פעולה זו תבטל את כל ההזמנות שנמצאות בסטטוס "ממתין לתשלום" מעל 24 שעות ללא תשלום מצורף.
                      לא ניתן לבטל פעולה זו.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>ביטול</AlertDialogCancel>
                    <AlertDialogAction onClick={cleanupStaleOrders}>
                      המשך
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">כל הסטטוסים</SelectItem>
                  <SelectItem value="pending">ממתין לתשלום</SelectItem>
                  <SelectItem value="processing">בטיפול</SelectItem>
                  <SelectItem value="ready_for_pickup">מוכן לאיסוף</SelectItem>
                  <SelectItem value="shipped">נשלח</SelectItem>
                  <SelectItem value="completed">הושלם</SelectItem>
                  <SelectItem value="cancelled">בוטל</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {searchQuery ? 'לא נמצאו הזמנות' : 'אין הזמנות עדיין'}
              </h3>
              <p className="text-muted-foreground">
                {searchQuery ? 'נסה לחפש במילים אחרות' : 'כאשר לקוחות יבצעו הזמנות, הן יופיעו כאן'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="rounded-md border">
                <Table dir="rtl">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">מספר הזמנה</TableHead>
                      <TableHead className="whitespace-nowrap">לקוח</TableHead>
                      <TableHead className="whitespace-nowrap">סטטוס הזמנה</TableHead>
                      <TableHead className="whitespace-nowrap">מוצרים</TableHead>
                      <TableHead className="whitespace-nowrap">סכום כולל</TableHead>
                      <TableHead className="whitespace-nowrap">אספקה</TableHead>
                      <TableHead className="whitespace-nowrap">תאריך הזמנה</TableHead>
                      <TableHead className="whitespace-nowrap">פעולות</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="text-right font-mono">
                          #{formatOrderNumber(order.order_number) || order.id.slice(-6)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div>
                            <div className="font-medium">
                              {order.profiles?.first_name && order.profiles?.last_name
                                ? truncateString(`${order.profiles.first_name} ${order.profiles.last_name}`, 20)
                                : (order.billing_address?.firstName || order.billing_address?.lastName || order.billing_address?.first_name || order.billing_address?.last_name)
                                  ? truncateString(`${order.billing_address?.firstName || order.billing_address?.first_name || ''} ${order.billing_address?.lastName || order.billing_address?.last_name || ''} (אורח)`, 20)
                                  : 'לא הוזן שם'
                              }
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {order.profiles?.email || order.billing_address?.email || ''}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="space-y-2">
                            {getOrderStatusBadge(order.status as OrderStatus)}
                            <select
                              value={order.status}
                              onChange={(e) => updateOrderStatus(order.id, e.target.value as OrderStatus)}
                              className="mt-1 text-xs border rounded px-1 w-full"
                              dir="rtl"
                            >
                              {getAllowedStatuses(order).map(status => (
                                <option key={status} value={status}>
                                  {getStatusLabel(status)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="space-y-1">
                            <div className="font-medium">
                              {order.sub_orders?.length || 0} מוצרים
                            </div>
                            <div className="text-xs text-muted-foreground max-w-32 truncate">
                              {order.sub_orders?.map(sub => sub.products.name).join(', ')}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              סה"כ כמות: {order.sub_orders?.reduce((sum, sub) => sum + (sub.quantity || 0), 0)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ₪{order.total_amount.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {order.sub_orders && order.sub_orders.length > 0 && order.sub_orders.every(sub => sub.delivery_option === 'pickup_only')
                            ? 'איסוף עצמי'
                            : `₪${order.total_delivery_cost.toLocaleString()}`
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="text-sm">
                            <div>{new Date(order.created_at).toLocaleDateString('he-IL')}</div>
                            <div className="text-xs text-muted-foreground">
                              {new Date(order.created_at).toLocaleTimeString('he-IL', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewOrder(order.id)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  disabled={deletingOrderId === order.id}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent dir="rtl">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>מחיקת הזמנה</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    האם אתה בטוח שברצונך למחוק את הזמנה #{order.order_number || order.id.slice(-6)}?
                                    <br /><br />
                                    <strong className="text-destructive">אזהרה: פעולה זו תמחק את:</strong>
                                    <ul className="list-disc list-inside mt-2 text-sm">
                                      <li>כל פריטי ההזמנה (תת-הזמנות)</li>
                                      <li>כל רישומי התשלום הקשורים</li>
                                      <li>את ההזמנה הראשית</li>
                                    </ul>
                                    <br />
                                    <strong>פעולה זו אינה ניתנת לביטול!</strong>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>ביטול</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteOrder(
                                      order.id,
                                      order.order_number || order.id.slice(-6)
                                    )}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    מחק לצמיתות
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <OrderDetailModal
        orderId={selectedOrderId}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onOrderUpdated={fetchOrders}
      />

      <OrderCreateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onOrderCreated={fetchOrders}
      />
    </div>
  );
};

export default OrdersModule;